import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface MyEvent {
  id: string;
  registrationId: string; // Unique registration ID for multiple registrations in same event
  title: string;
  organizer: string;
  sport: string;
  startDate: string;
  endDate: string | null;
  time: string;
  location: string;
  category: string;
  subcategory?: string;
  partner?: string;
  status: "upcoming" | "active" | "finished";
  inscriptionStatus?: string;
  participants?: number;
  maxParticipants?: number;
  registrationFee?: number;
  matchType?: "singles" | "doubles" | "ambos";
  tournamentFormat?: 'round-robin' | 'round_robin' | 'sumatoria-juegos' | 'sumatoria_juegos' | 'eliminacion-directa' | 'eliminacion_directa';
  isLeague?: boolean;
  leagueFormat?: 'round-robin' | 'round_robin' | 'sumatoria-juegos' | 'sumatoria_juegos' | 'escalera-piramidal' | 'escalera_piramidal';
  duration?: string;
  matchesPerWeek?: string;
  userName?: string;
  imageUrl?: string;
  organizerId?: string;
}

// Function to calculate event status based on dates
const calculateEventStatus = (startDate: string, endDate: string | null): "upcoming" | "active" | "finished" => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Start of current day
  
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);
  
  // If endDate exists, use it; otherwise use startDate as end date
  const end = endDate ? new Date(endDate) : new Date(startDate);
  end.setHours(23, 59, 59, 999); // End of the day
  
  if (today < start) {
    // Before start date = Upcoming
    return 'upcoming';
  } else if (today >= start && today <= end) {
    // From start date to end date = Active
    return 'active';
  } else {
    // After end date = Finished
    return 'finished';
  }
};

export function useMyEvents(targetUserId?: string) {
  const { user } = useAuth();
  
  // Use targetUserId if provided, otherwise use current user
  const effectiveUserId = targetUserId || user?.id;
  const [myEvents, setMyEvents] = useState<{
    upcoming: MyEvent[];
    active: MyEvent[];
    finished: MyEvent[];
  }>({
    upcoming: [],
    active: [],
    finished: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMyEvents = async () => {
    console.log('=== useMyEvents - Fetching for user ===');
    console.log('Effective User ID:', effectiveUserId);
    
    if (!effectiveUserId) {
      console.warn('No user ID available, returning empty events');
      setMyEvents({ upcoming: [], active: [], finished: [] });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch registrations where user is either player1 (user_id) or player2 (partner_user_id)
      // Also fetch player1_name and player2_name for manual registrations
      const { data, error } = await supabase
        .from('registrations')
        .select(`
          *,
          events!inner(
            id,
            title,
            sport,
            location,
            start_date,
            end_date,
            status,
            event_type,
            tournament_format,
            league_format,
            max_participants,
            entry_fee,
            start_time,
            match_type,
            category,
            subcategory,
            organizer_id,
            image_url
          )
        `)
        .or(`user_id.eq.${effectiveUserId},partner_user_id.eq.${effectiveUserId}`)
        .eq('status', 'confirmed')
        .order('registered_at', { ascending: false });

      if (error) throw error;

      // Get organizer names in a separate query
      const organizerIds = [...new Set(data?.map(r => r.events.organizer_id).filter(Boolean))] as string[];
      let organizerNames: Record<string, string> = {};

      if (organizerIds.length > 0) {
        const { data: organizersData } = await supabase
          .from('organizers')
          .select('id, company_name')
          .in('id', organizerIds);
        
        if (organizersData) {
          organizerNames = organizersData.reduce((acc, o) => {
            acc[o.id] = o.company_name || 'Organizador';
            return acc;
          }, {} as Record<string, string>);
        }
      }

      // Get partner names - need to check both directions (user as player1 or player2)
      const partnerUserIds = new Set<string>();
      
      data?.forEach(r => {
        if (r.user_id === effectiveUserId && r.partner_user_id) {
          // User is player1, partner is player2
          partnerUserIds.add(r.partner_user_id);
        } else if (r.partner_user_id === effectiveUserId && r.user_id) {
          // User is player2, partner is player1
          partnerUserIds.add(r.user_id);
        }
      });

      let partnerNames: Record<string, string> = {};
      
      if (partnerUserIds.size > 0) {
        const { data: partnersData } = await supabase
          .from('profiles')
          .select('user_id, full_name')
          .in('user_id', Array.from(partnerUserIds));
        
        if (partnersData) {
          partnerNames = partnersData.reduce((acc, p) => {
            acc[p.user_id] = p.full_name;
            return acc;
          }, {} as Record<string, string>);
        }
      }

      const transformedEvents: MyEvent[] = data?.map((registration: any) => {
        const event = registration.events;
        
        // Format category and subcategory properly - use registration data first
        const category = registration.category || event.category || "Sin categoría";
        const subcategory = registration.subcategory || event.subcategory;
        
        // Combine category and subcategory into a single formatted string
        let fullCategory = category;
        if (subcategory && category !== "Sin categoría") {
          fullCategory = `${category} ${subcategory}`;
        }
        
        // Determine partner based on user's role in the registration
        // Priority: playerX_name (manual entry) > profiles lookup
        let partnerName: string | undefined;
        const isPlayer1 = registration.user_id === effectiveUserId;
        
        if (isPlayer1) {
          // User is player1, show player2 as partner
          // Priority: player2_name (manual) > profiles lookup
          if (registration.player2_name) {
            partnerName = registration.player2_name;
          } else if (registration.partner_user_id) {
            partnerName = partnerNames[registration.partner_user_id];
          }
        } else {
          // User is player2, show player1 as partner
          // Priority: player1_name (manual) > profiles lookup
          if (registration.player1_name) {
            partnerName = registration.player1_name;
          } else if (registration.user_id) {
            partnerName = partnerNames[registration.user_id];
          }
        }

        console.log('[useMyEvents] Processing registration:', {
          registrationId: registration.id,
          eventId: event.id,
          eventTitle: event.title,
          userId: effectiveUserId,
          isPlayer1,
          player1_name: registration.player1_name,
          player2_name: registration.player2_name,
          partnerName,
          category: fullCategory,
          registrationCategory: registration.category,
          registrationSubcategory: registration.subcategory
        });
        
        return {
          id: event.id,
          registrationId: registration.id, // Add unique registration ID
          title: event.title,
          organizer: event.organizer_id ? (organizerNames[event.organizer_id] || "Organizador") : "Organizador",
          sport: event.sport,
          startDate: event.start_date,
          endDate: event.end_date,
          time: event.start_time || "09:00",
          location: event.location,
          category: fullCategory,
          subcategory: subcategory,
          partner: partnerName,
          status: calculateEventStatus(event.start_date, event.end_date),
          inscriptionStatus: registration.payment_status,
          maxParticipants: event.max_participants,
          registrationFee: event.entry_fee,
          matchType: registration.modality === 'dobles' ? 'doubles' : 'singles',
          tournamentFormat: event.tournament_format,
          isLeague: event.event_type === 'league',
          leagueFormat: event.league_format,
          userName: user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Usuario',
          imageUrl: event.image_url,
          organizerId: event.organizer_id
        };
      }) || [];

      // Group events by status
      const groupedEvents = {
        upcoming: transformedEvents.filter(e => e.status === 'upcoming'),
        active: transformedEvents.filter(e => e.status === 'active'),
        finished: transformedEvents.filter(e => e.status === 'finished')
      };

      setMyEvents(groupedEvents);
    } catch (err) {
      console.error('Error fetching my events:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar eventos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyEvents();
  }, [effectiveUserId]);

  return {
    myEvents,
    loading,
    error,
    refetch: fetchMyEvents
  };
}